import Badge, { BadgeProps } from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Header from "@cloudscape-design/components/header"
import Icon from "@cloudscape-design/components/icon"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { StudyProtocolStatusEnum } from "@/generated/sdk"
import ProtocolAlert from "../../../components/ProtocolAlert"
import UploadButton from "../../../components/UploadButton"
import { StudyProtocol } from "../../../services/studyProtocolApi"
import AssessmentsGrid from "./AssessmentsGrid"
import { DEFAULT_FORM_VALUES, generateVisitId, partSectionLabel } from "./constants"
import { ProtocolFormData } from "./schema"

const STATUS_BADGE: Record<StudyProtocolStatusEnum, { label: string; color: BadgeProps["color"] }> =
  {
    [StudyProtocolStatusEnum.NotStarted]: { label: "Not started", color: "grey" },
    [StudyProtocolStatusEnum.InProgress]: { label: "In progress", color: "blue" },
    [StudyProtocolStatusEnum.PendingReview]: { label: "Pending review", color: "blue" },
    [StudyProtocolStatusEnum.Approved]: { label: "Approved", color: "green" },
    [StudyProtocolStatusEnum.Failed]: { label: "Failed", color: "red" },
  }

interface ProtocolApprovedViewProps {
  /**
   * Study ID for navigation links.
   */
  studyId: string
  /**
   * Full protocol object with data and metadata.
   */
  protocol: StudyProtocol
  /**
   * Whether an upload is currently in progress.
   */
  uploading: boolean
  /**
   * Error message from a failed upload attempt.
   */
  uploadError: string | null
  /**
   * Callback to trigger file picker.
   */
  onUploadClick: () => void
}

const formatDate = (d: string) => {
  try {
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return d
  }
}

/**
 * Displays an approved protocol's details.
 *
 * Shows protocol metadata (title, version, sponsor, phase) and the Schedule of Activities grid in
 * read-only mode. Provides actions to edit the protocol or re-upload a new PDF.
 */
const ProtocolApprovedView = ({
  studyId,
  protocol,
  uploading,
  uploadError,
  onUploadClick,
}: ProtocolApprovedViewProps) => {
  const navigate = useNavigate()

  const form = useForm<ProtocolFormData>({
    defaultValues: {
      ...DEFAULT_FORM_VALUES,
      study_title: protocol.data?.study_title || "",
      sponsor: protocol.data?.sponsor || "",
      phase: protocol.data?.phase || "",
      parts: protocol.data?.parts?.length
        ? protocol.data.parts.map((part) => ({
            ...part,
            periods: part.periods.map((period) => ({
              ...period,
              visits: period.visits.map((visit) => ({
                ...visit,
                id: visit.id || generateVisitId(),
                visit_number: String(visit.visit_number ?? ""),
              })),
            })),
          }))
        : DEFAULT_FORM_VALUES.parts,
    },
  })

  const meta = [
    protocol.data?.phase,
    protocol.data?.sponsor && `Sponsor: ${protocol.data.sponsor}`,
    `Created: ${formatDate(protocol.created_at)}`,
  ]
    .filter(Boolean)
    .join("  •  ")

  const status = STATUS_BADGE[protocol.status]

  return (
    <SpaceBetween size="l">
      <Header
        variant="h2"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button iconName="edit" onClick={() => navigate(`/studies/${studyId}/protocol/edit`)}>
              Edit
            </Button>
            <UploadButton
              uploading={uploading}
              onClick={onUploadClick}
              label="Re-upload"
              icon={<Icon name="upload" size="normal" />}
            />
          </SpaceBetween>
        }
        description={protocol.data?.study_title || undefined}
      >
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <span>Study Protocol</span>
          <Badge color={status.color}>{status.label.toUpperCase()}</Badge>
        </SpaceBetween>
      </Header>

      <Box color="text-body-secondary" fontSize="body-s">
        {meta}
      </Box>

      {uploadError && <ProtocolAlert message={uploadError} />}

      {(protocol.data?.parts?.length ? protocol.data.parts : [null]).map((part, index, all) => (
        <AssessmentsGrid
          key={index}
          form={form}
          partIndex={index}
          readOnly
          embedded
          partLabel={partSectionLabel(part?.name, index, all.length)}
        />
      ))}
    </SpaceBetween>
  )
}

export default ProtocolApprovedView
